import { Component, DOCUMENT, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideBookOpen,
  lucideBug,
  lucideFullscreen,
  lucideGlobe,
  lucideKeyRound,
  lucideLaptop,
  lucideLightbulb,
  lucideLogOut,
  lucideMinimize,
  lucideMoon,
  lucidePalette,
  lucideSun,
} from '@ng-icons/lucide';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { HlmAvatarImports } from '@spartan-ng/helm/avatar';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmToggleGroupImports } from '@spartan-ng/helm/toggle-group';
import { fromEvent, map, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AppHelp } from '../../models/app-help.model';
import { AppService, ThemePreference } from '../../services/app.service';
import { AuthService } from '../../services/auth.service';
import { HelpService } from '../../services/help.service';

interface LanguageOption {
  value: string;
  label: string;
  flag: string;
}

interface ThemeOption {
  value: ThemePreference;
  icon: string;
  labelKey: string;
}

/**
 * Profile menu behind the avatar in the app header (Figma "User menu", node 83:2727): who is
 * signed in, fullscreen, language, help links, theme and sign out.
 */
@Component({
  selector: 'app-user-menu',
  templateUrl: './user-menu.html',
  imports: [
    RouterLink,
    NgIcon,
    TranslatePipe,
    HlmAvatarImports,
    HlmButtonImports,
    HlmDropdownMenuImports,
    HlmToggleGroupImports,
  ],
  providers: [
    HelpService,
    provideIcons({
      lucideBookOpen,
      lucideBug,
      lucideFullscreen,
      lucideGlobe,
      lucideKeyRound,
      lucideLaptop,
      lucideLightbulb,
      lucideLogOut,
      lucideMinimize,
      lucideMoon,
      lucidePalette,
      lucideSun,
    }),
  ],
})
export class UserMenu {
  private readonly authService = inject(AuthService);
  private readonly appService = inject(AppService);
  private readonly translate = inject(TranslateService);
  private readonly document = inject(DOCUMENT);

  public readonly displayName = computed(() => this.authService.displayName());
  public readonly email = computed(() => this.authService.oktaUser()?.email ?? null);
  public readonly initials = computed(() =>
    this.displayName()?.split(' ').filter(Boolean).map(part => part.substring(0, 1)).join('').toUpperCase() || '');

  public readonly languages: readonly LanguageOption[] = [
    { value: 'en-US', label: 'English', flag: 'images/flags/us.svg' },
    { value: 'pl-PL', label: 'Polski', flag: 'images/flags/pl.svg' },
  ];
  public readonly language = signal(this.translate.getCurrentLang() || localStorage.getItem('language') || 'pl-PL');

  public readonly themes: readonly ThemeOption[] = [
    { value: 'light', icon: 'lucideSun', labelKey: 'light' },
    { value: 'dark', icon: 'lucideMoon', labelKey: 'dark' },
    { value: 'system', icon: 'lucideLaptop', labelKey: 'system' },
  ];
  public readonly theme = this.appService.themePreference;

  public readonly isFullscreen = signal(!!this.document.fullscreenElement);

  /** Help entries the menu links to, read from the same file as the Help page. */
  private readonly helpLinks = toSignal(
    inject(HelpService).getAll().pipe(
      map(items => items.filter((item): item is AppHelp & { href: string } => !!item.menuItem && !!item.href)),
      catchError(() => of([])),
    ),
    { initialValue: [] },
  );
  public readonly getStartedLink = computed(() => this.linkFor('getStarted'));
  public readonly suggestionLink = computed(() => this.linkFor('suggestion'));
  public readonly reportBugLink = computed(() => this.linkFor('reportBug'));

  constructor() {
    fromEvent(this.document, 'fullscreenchange')
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.isFullscreen.set(!!this.document.fullscreenElement));
  }

  toggleFullscreen(): void {
    if (this.document.fullscreenElement) {
      void this.document.exitFullscreen();
    } else {
      void this.document.documentElement.requestFullscreen();
    }
  }

  setLanguage(language: string): void {
    if (language === this.language()) {
      return;
    }
    localStorage.setItem('language', language);
    this.language.set(language);
    this.translate.use(language);
  }

  setTheme(theme: unknown): void {
    // The toggle group reports `null` when the active item is clicked again; keep the choice.
    if (theme === 'light' || theme === 'dark' || theme === 'system') {
      this.appService.setTheme(theme);
    }
  }

  logout(): void {
    void this.authService.signout();
  }

  private linkFor(menuItem: AppHelp['menuItem']): string | null {
    return this.helpLinks().find(item => item.menuItem === menuItem)?.href ?? null;
  }
}
